import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  requestPresupuestoDetalles,
  requestEliminarPresupuestoDetalle,
  requestCancelarPresupuesto,
  requestEditarPresupuesto,
  resetReceivePreconfirmPresupuestoStatus,
  requestVaciarCarrito,
  requestCantInCarrito,
  resetVaciarCarritoStatus,
} from "../../actions/presupuesto";
import Dolar from "../../common/dolar";
import rutas from "../../routes/constants/routes";
import colores from "../../shared/constants/styles";
import { Button, Dialog, DialogActions, DialogTitle } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import MyUserService from "../../services/myUser";
import { isAllowed } from "../../shared/utils/permissions";
import ShopRowOut from "./shopRowOut";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ShopRow from "./ShopRow";
import ShopRowTitle from "./ShopRowTitle";
import LoadingCart from "./LoadingCart";
import Alert from "@mui/material/Alert";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface Producto {
  peso: number;
}

interface PresupuestoDetalle {
  item: number;
  presupuestoId: number;
  cantidadTiras: number;
  cantMin: number;
  precio: number;
  dimension1: number;
  dimension2: number;
  descuento1: number;
  montoSubtotal: number;
  montoSubtotalDto: number;
  pesoSubtotal: number;
  precioTotalActualizado?: number;
  pesoTotalActualizado?: number;
  precioDtoActualizado?: number;
  activo: boolean;
  producto?: Producto;
}

interface Moneda {
  simbolo: string;
}

interface Presupuesto {
  id: number;
  usuarioAlta: number;
  vendedorId: number;
  clienteId: number;
  descuentoCliente?: number;
  moneda?: Moneda;
  presupuestoDetalles?: {
    $values: PresupuestoDetalle[];
  };
}

interface Menu {
  nombre: string;
  menuPadreId?: number;
  link: string | null;
}

interface MyUser {
  contactoId?: number;
  clienteId?: number;
  tipoRol?: number;
  menus?: {
    $values: Menu[];
  };
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface CarritoProps {
  presupuesto: Presupuesto;
  isCancel: boolean;
  loading: boolean;
  error?: ApiError;
  vaciarCarritoSuccess?: boolean;
}

interface RootState {
  presupuestos: {
    presupuesto: Presupuesto;
    isCancel: boolean;
    loading: boolean;
    error?: ApiError;
    vaciarCarritoSuccess?: boolean;
  };
}

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

const Carrito: React.FC<CarritoProps> = (props) => {
  const { presupuesto, isCancel } = props;

  const dispatch = useDispatch<any>();
  const history = useHistory();

  const myUser = MyUserService.getMyUser() as MyUser | null;

  const seguirComprandoProductos = myUser?.menus?.$values.find((m) =>
    m.nombre.includes("Productos"),
  );

  const seguirComprandoGrupo = myUser?.menus?.$values.find(
    (m) =>
      m.menuPadreId === seguirComprandoProductos?.menuPadreId &&
      m.link !== null,
  );

  const seguirComprando =
    (seguirComprandoProductos?.link ?? "") + (seguirComprandoGrupo?.link ?? "");

  const [precioSubtotal, setPrecioSubtotal] = useState<number | null>(null);
  const [precioTotal, setPrecioTotal] = useState<number | null>(null);
  const [pesoSubtotal, setPesoSubtotal] = useState<number | null>(null);
  const [actualizarCarrito, setActualizarCarrito] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [botonBloqueado, setBotonBloqueado] = useState<boolean>(false);
  const [botonError, setBotonError] = useState<boolean>(false);
  const [openVaciarCarrito, setOpenVaciarCarrito] = useState<boolean>(false);
  const [carritoVacio, setCarritoVacio] = useState<boolean>(false);
  const [detalles, setDetalles] = useState<PresupuestoDetalle[] | undefined>(
    [],
  );
  const [cantArticulos, setCantArticulos] = useState<number>(0);
  const [datosCargados, setDatosCargados] = useState<boolean>(false);
  /* ------------------------------------------------------------------------ */
  /*                          Cálculos del carrito                            */
  /* ------------------------------------------------------------------------ */

  const redondear = (monto: number): number => {
    return Math.round((monto + Number.EPSILON) * 100) / 100;
  };

  const calculoMontoTotalSinDto = (items?: PresupuestoDetalle[]): number => {
    let totalAmout = 0;

    if (items !== undefined) {
      for (let i = 0; i < items.length; i++) {
        totalAmout = items[i].montoSubtotal + totalAmout;
      }
    } else {
      return 0;
    }

    return redondear(totalAmout);
  };

  const calculoMontolConDto = (items?: PresupuestoDetalle[]): number => {
    let totalAmoutDTo = 0;

    if (items !== undefined) {
      for (let i = 0; i < items.length; i++) {
        totalAmoutDTo = items[i].montoSubtotalDto + totalAmoutDTo;
      }
    } else {
      return 0;
    }

    return redondear(totalAmoutDTo);
  };

  const calculoPeso = (items?: PresupuestoDetalle[]): number => {
    let total = 0;

    if (items !== undefined) {
      for (let i = 0; i < items.length; i++) {
        total = items[i].pesoSubtotal + total;
      }
    } else {
      return total;
    }

    return total;
  };

  const buscarInactivo = (items?: PresupuestoDetalle[]): void => {
    setBotonError(false);

    let encontrado = false;

    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].activo) {
          encontrado = true;
        }
      }
    }

    setBotonBloqueado(encontrado);
  };

  /* ------------------------------------------------------------------------ */
  /*                             Totales                                      */
  /* ------------------------------------------------------------------------ */

  const montoTotalCarrito = calculoMontoTotalSinDto(detalles);
  const montoTotalDescuento = calculoMontolConDto(detalles);
  const montoDescuento = redondear(montoTotalCarrito - montoTotalDescuento);
  const pesoTotalAct = calculoPeso(detalles);

  /* ------------------------------------------------------------------------ */
  /*                         Cambio de cantidad                               */
  /* ------------------------------------------------------------------------ */

  const [isChange, setIsChange] = useState<boolean>(false);

  const handleChange = (count: number, item: number): void => {
    if (!isChange) {
      detalles?.forEach((d) => {
        d.precioTotalActualizado = d.precioTotalActualizado ?? d.montoSubtotal;

        d.pesoTotalActualizado = d.pesoTotalActualizado ?? d.pesoSubtotal;

        d.precioDtoActualizado = d.precioDtoActualizado ?? d.montoSubtotalDto;
      });
    }

    const det = detalles?.find((d) => d.item === item);

    if (!det) {
      return;
    }

    if (count <= 0 || count < det.cantMin) {
      setBotonBloqueado(true);
    } else {
      setBotonBloqueado(false);

      det.cantidadTiras = count;

      det.precioTotalActualizado =
        det.cantidadTiras *
        det.precio *
        (det.dimension1 > 0 ? det.dimension1 : 1) *
        (det.dimension2 > 0 ? det.dimension2 : 1);

      det.pesoTotalActualizado =
        det.cantidadTiras *
        (det.producto?.peso ?? 0) *
        (det.dimension1 > 0 ? det.dimension1 : 1) *
        (det.dimension2 > 0 ? det.dimension2 : 1);

      det.precioDtoActualizado =
        det.descuento1 > 0
          ? det.precioTotalActualizado -
            (det.precioTotalActualizado * det.descuento1) / 100
          : det.precioTotalActualizado;

      setIsChange(true);

      let precioTotalDto = 0;
      let pesoTotal = 0;
      let precioDto = 0;

      if (isChange) {
        detalles?.forEach((d) => {
          d.precioTotalActualizado =
            d.precioTotalActualizado ?? d.montoSubtotal;

          d.pesoTotalActualizado = d.pesoTotalActualizado ?? d.pesoSubtotal;

          d.precioDtoActualizado = d.precioDtoActualizado ?? d.montoSubtotalDto;
        });
      }

      detalles?.forEach((d) => {
        precioTotalDto += d.precioTotalActualizado ?? 0;
        pesoTotal += d.pesoTotalActualizado ?? 0;
        precioDto += d.precioDtoActualizado ?? 0;
      });

      handleClick(presupuesto.id, false);

      setPrecioSubtotal(precioTotalDto);
      setPesoSubtotal(pesoTotal);
      setPrecioTotal(precioDto);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                Effects                                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (presupuesto?.presupuestoDetalles) {
      setDetalles(presupuesto?.presupuestoDetalles?.$values);

      const cantArticulos = presupuesto?.presupuestoDetalles?.$values
        ?.map(() => 1)
        .reduce((previous, current) => previous + current, 0);
      setCantArticulos(cantArticulos ?? 0);
      setDatosCargados(true);
    }
  }, [presupuesto]);

  useEffect(() => {
    if (props.error?.response?.data?.message) {
      setError(true);
    }
  }, [props.error?.response?.data?.message]);

  useEffect(() => {
    dispatch(resetReceivePreconfirmPresupuestoStatus());

    if (isAllowed([18, 40, 28, 42])) {
      dispatch(
        requestPresupuestoDetalles(myUser?.contactoId, myUser?.clienteId),
      );
    } else {
      setDatosCargados(true);
    }
  }, [actualizarCarrito]);

  useEffect(() => {
    buscarInactivo(detalles);
  }, [detalles]);

  useEffect(() => {
    if (props.vaciarCarritoSuccess) {
      setCarritoVacio(true);
      dispatch(resetVaciarCarritoStatus());
      setCantArticulos(0);
      dispatch(requestCantInCarrito(myUser?.contactoId, myUser?.clienteId));
    }
  }, [props.vaciarCarritoSuccess]);

  /* ------------------------------------------------------------------------ */
  /*                              Handlers                                    */
  /* ------------------------------------------------------------------------ */

  const handleClickCancelarCarrito = (presupuestoId: number): void => {
    dispatch(requestCancelarPresupuesto(presupuestoId));
    history.push(seguirComprando);
  };

  const handleClickSeguirComprando = (): void => {
    history.push(rutas.DASHBOARD);
  };

  const handleClick = (presupuestoId: number, changePage: boolean): void => {
    if (detalles !== undefined) {
      for (let i = 0; i < detalles.length; i++) {
        let total = detalles[i].montoSubtotal;

        if (detalles[i].descuento1 > 0) {
          detalles[i].descuento1 = redondear(detalles[i].descuento1);

          total = total - (total * detalles[i].descuento1) / 100;
        }

        if (presupuesto.descuentoCliente && presupuesto.descuentoCliente > 0) {
          total =
            total -
            (total *
              (presupuesto.descuentoCliente
                ? presupuesto.descuentoCliente
                : 1)) /
              100;
        }

        detalles[i].montoSubtotalDto = total;
        if (detalles[i].precioDtoActualizado !== undefined) {
          const precioDtoActualizado = detalles[i].precioDtoActualizado ?? 0;
          detalles[i].montoSubtotalDto =
            precioDtoActualizado *
            ((100 -
              (presupuesto.descuentoCliente
                ? presupuesto.descuentoCliente
                : 0)) /
              100);
          detalles[i].montoSubtotal = detalles[i].precioTotalActualizado ?? 0;

          detalles[i].pesoSubtotal = detalles[i].pesoTotalActualizado ?? 0;
        }
      }
    }

    const presupuestoActualizado = {
      id: presupuesto.id,
      usuarioAlta: presupuesto.usuarioAlta,
      vendedorId: presupuesto.vendedorId,
      clienteId: presupuesto.clienteId,
      presupuestoDetalles: detalles ?? [],
    };

    if (changePage === true) {
      if (!props.loading) {
        history.push({
          pathname: `${rutas.ENVIOPAGO}/${presupuestoId}`,
          state: { detalles },
        });
      }
    } else {
      dispatch(requestEditarPresupuesto(presupuestoActualizado, presupuestoId));
    }
  };

  const handleClickEliminar = (presupuestoId: number, item: number): void => {
    dispatch(requestEliminarPresupuestoDetalle(presupuestoId, item));
    setCantArticulos(cantArticulos - 1);
  };

  const handleOpenVaciarCarrito = (): void => {
    setOpenVaciarCarrito(true);
  };

  const handleCloseVaciarCarrito = (): void => {
    setOpenVaciarCarrito(false);
  };

  const handleConfirmarVaciarCarrito = (): void => {
    dispatch(requestVaciarCarrito(presupuesto.id));
    setOpenVaciarCarrito(false);
  };

  /* ------------------------------------------------------------------------ */
  /*                               Dialog                                     */
  /* ------------------------------------------------------------------------ */

  const [open, setOpen] = useState<boolean>(false);

  const theme = useTheme();

  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  /* ------------------------------------------------------------------------ */
  /*                                Render                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      style={{
        backgroundColor: colores.grisClaro,
        overflow: "hidden",
        clear: "both",
      }}
    >
      <Dolar />

      <div className="d-flex justify-content-center">
        <hr
          style={{
            width: "90%",
            color: "black",
          }}
        />
      </div>

      <div
        className="d-flex justify-content-between flex-wrap align-items-center"
        style={{
          marginBottom: "0.600rem",
          paddingRight: "3rem",
          paddingLeft: "21px",
        }}
      >
        <h1
          className="fw-bolder"
          style={{
            margin: "1rem 3.5rem",
          }}
        >
          MI CARRITO
        </h1>
      </div>

      {props.loading || !datosCargados ? (
        <LoadingCart />
      ) : (
        <div
          style={{
            backgroundColor: "white",
            overflow: "hidden",
            height: "auto",
          }}
        >
          {cantArticulos >= 1 ? (
            <div>
              <div
                className="row justify-content-center"
                style={{
                  marginTop: "1rem",
                }}
              >
                <div
                  className="col-12 col-sm-8 col-md-8 col-lg-8"
                  style={{
                    overflowX: "auto",
                  }}
                >
                  <div className="ShoppingCart__item-list">
                    <ShopRowTitle />

                    {detalles?.map((detalle) =>
                      detalle?.activo ? (
                        <ShopRow
                          title={false}
                          key={detalle.item}
                          data={detalle}
                          precioTotalActualizado={detalle.montoSubtotal}
                          pesoTotalActualizado={detalle.pesoSubtotal}
                          handleClickEliminar={() => {
                            handleClickEliminar(
                              detalle.presupuestoId,
                              detalle.item,
                            );
                          }}
                          handleChange={(e: number) => {
                            handleChange(e, detalle.item);
                          }}
                        />
                      ) : (
                        <ShopRowOut
                          key={detalle.item}
                          data={detalle}
                          handleClickEliminar={() => {
                            handleClickEliminar(
                              detalle.presupuestoId,
                              detalle.item,
                            );
                          }}
                        />
                      ),
                    )}
                  </div>
                </div>

                <div className="col-12 col-sm-3 col-md-3 col-lg-3">
                  <div
                    style={{
                      borderRadius: "5px",
                      backgroundColor: "#F4F4F4",
                      padding: "1.5rem 1.5rem",
                    }}
                  >
                    <h4>Resumen de compra</h4>

                    <hr
                      style={{
                        backgroundColor: "black",
                        width: "100%",
                        height: "1px",
                        border: 0,
                      }}
                    />

                    {/* Subtotal sin descuento */}
                    <div className="d-flex justify-content-between">
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        Subtotal
                      </p>

                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {presupuesto?.moneda?.simbolo}{" "}
                        {montoTotalCarrito.toFixed(2)}
                      </p>
                    </div>

                    <hr
                      style={{
                        backgroundColor: "black",
                        width: "100%",
                        height: "1px",
                        border: 0,
                      }}
                    />

                    {/* Peso */}
                    <div className="d-flex justify-content-between">
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        Peso subtotal
                      </p>

                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {pesoSubtotal === null ||
                        precioSubtotal === null ||
                        precioSubtotal <= 0
                          ? pesoTotalAct.toFixed(2)
                          : pesoSubtotal.toFixed(2)}{" "}
                        KG
                      </p>
                    </div>

                    <hr
                      style={{
                        backgroundColor: "black",
                        width: "100%",
                        height: "1px",
                        border: 0,
                      }}
                    />

                    {/* Descuento total */}
                    <div className="d-flex justify-content-between">
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        Descuento
                      </p>

                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {presupuesto?.moneda?.simbolo}{" "}
                        {montoDescuento.toFixed(2)}
                      </p>
                    </div>

                    <hr
                      style={{
                        backgroundColor: "black",
                        width: "100%",
                        height: "1px",
                        border: 0,
                      }}
                    />

                    {/* Total con descuento */}
                    <div className="d-flex justify-content-between">
                      <h4
                        style={{
                          color: "#E3000B",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        Total
                      </h4>

                      <h4
                        style={{
                          color: "#E3000B",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {presupuesto?.moneda?.simbolo}{" "}
                        {redondear(montoTotalDescuento).toFixed(2)}
                      </h4>
                    </div>

                    <div
                      className="d-flex justify-content-around"
                      style={{
                        backgroundColor: "#F4F4F4",
                        marginTop: "2rem",
                      }}
                    >
                      {botonBloqueado ? (
                        <Button
                          style={{
                            backgroundColor: "#a9a9a9",
                            color: "#fff",
                            width: "90%",
                            height: "3.5rem",
                            fontSize: "18px",
                          }}
                          variant="contained"
                          onClick={() => {
                            setBotonError(true);
                          }}
                        >
                          Comprar
                        </Button>
                      ) : (
                        <Button
                          style={{
                            backgroundColor: colores.rojo,
                            color: "#fff",
                            width: "90%",
                            height: "3.5rem",
                            fontSize: "18px",
                          }}
                          variant="contained"
                          onClick={() => handleClick(presupuesto.id, true)}
                        >
                          Comprar
                        </Button>
                      )}
                    </div>

                    {botonError && (
                      <p
                        style={{
                          color: colores.rojo,
                          marginTop: "10px",
                        }}
                      >
                        Debes eliminar del carrito los productos que ya no
                        tienes disponible
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                {isAllowed(17) ? (
                  <div
                    className="d-flex justify-content-around"
                    style={{
                      width: "100%",
                      marginBottom: "2rem",
                    }}
                  >
                    <div className="col-12 col-sm-8 col-md-8 col-lg-8">
                      <div className="d-flex justify-content-around">
                        {myUser?.tipoRol === 2 && cantArticulos >= 1 ? (
                          <>
                            <button
                              className="btn btn-outline my-3"
                              style={{
                                backgroundColor: colores.rojo,
                                color: "#fff",
                                textTransform: "uppercase",
                              }}
                              type="button"
                              onClick={handleOpen}
                            >
                              Cancelar Carrito
                            </button>

                            <Dialog
                              fullScreen={fullScreen}
                              open={open}
                              onClose={handleClose}
                              aria-labelledby="responsive-dialog-title"
                              style={{
                                fontFamily: "Mada",
                              }}
                            >
                              <DialogTitle id="responsive-dialog-title">
                                {
                                  "¿Estás seguro que querés cancelar el presupuesto?"
                                }
                              </DialogTitle>

                              <DialogActions className="d-flex justify-content-around">
                                <Button
                                  autoFocus
                                  onClick={handleClose}
                                  className="btn btn-light my-3 mx-3"
                                  style={{
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Cerrar
                                </Button>

                                <button
                                  className="btn btn-outline-secondary my-3 mx-3"
                                  style={{
                                    textTransform: "uppercase",
                                  }}
                                  onClick={() =>
                                    handleClickCancelarCarrito(presupuesto.id)
                                  }
                                >
                                  Cancelar carrito
                                </button>
                              </DialogActions>
                            </Dialog>
                          </>
                        ) : (
                          ""
                        )}

                        <div
                          className="d-flex justify-content-around"
                          style={{
                            width: "100%",
                            marginBottom: "2rem",
                          }}
                        >
                          <div className="col-12 col-sm-8 col-md-8 col-lg-8">
                            {!carritoVacio &&
                              cantArticulos !== undefined &&
                              cantArticulos >= 1 && (
                                <Button
                                  variant="outlined"
                                  onClick={handleOpenVaciarCarrito}
                                  style={{
                                    color: colores.rojo,
                                    borderColor: colores.rojo,
                                    marginRight: "3.5rem",
                                    marginTop: "1rem",
                                    marginBottom: "1rem",
                                    height: "3rem",
                                  }}
                                >
                                  VACIAR CARRITO
                                </Button>
                              )}
                            <Button
                              variant="outlined"
                              className="btn btn-outline-success my-3"
                              style={{
                                color: colores.verde,
                                borderColor: colores.verde,
                                marginRight: "3.5rem",
                                marginTop: "1rem",
                                marginBottom: "1rem",
                                height: "3rem",
                              }}
                              onClick={() => handleClickSeguirComprando()}
                            >
                              Seguir Comprando
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div
              style={{
                minHeight: "20rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "3rem 1rem",
              }}
            >
              <ShoppingCartOutlinedIcon
                style={{
                  fontSize: "80px",
                  color: "#bdbdbd",
                  marginBottom: "1rem",
                }}
              />

              <h4
                style={{
                  color: "#333",
                  fontWeight: 600,
                  marginBottom: "0.75rem",
                }}
              >
                Tu carrito está vacío
              </h4>

              <p
                style={{
                  color: "#777",
                  marginBottom: "1.5rem",
                  fontSize: "16px",
                }}
              >
                No hay ningún artículo en tu carrito de compras.
              </p>

              <Button
                variant="contained"
                onClick={handleClickSeguirComprando}
                style={{
                  backgroundColor: colores.rojo,
                  color: "#fff",
                  padding: "0.7rem 2rem",
                  textTransform: "uppercase",
                  fontSize: "14px",
                }}
              >
                Seguir comprando
              </Button>
            </div>
          )}

          {error && (
            <Alert
              style={{
                textAlign: "center",
                margin: "2vw",
              }}
              severity="error"
            >
              {props.error?.response?.data?.message}
            </Alert>
          )}
        </div>
      )}
      <Dialog
        open={openVaciarCarrito}
        onClose={handleCloseVaciarCarrito}
        aria-labelledby="vaciar-carrito-dialog-title"
      >
        <DialogTitle id="vaciar-carrito-dialog-title">
          ¿Estás seguro que querés vaciar el carrito?
        </DialogTitle>

        <DialogActions className="d-flex justify-content-around">
          <Button
            onClick={handleCloseVaciarCarrito}
            className="btn btn-light my-3 mx-3"
            style={{
              textTransform: "uppercase",
            }}
          >
            Cerrar
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirmarVaciarCarrito}
            style={{
              backgroundColor: colores.rojo,
              color: "#fff",
              textTransform: "uppercase",
            }}
            className="my-3 mx-3"
          >
            Vaciar carrito
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Redux                                         */
/* -------------------------------------------------------------------------- */

const mapStateToProps = (state: RootState) => ({
  presupuesto: state.presupuestos.presupuesto,
  isCancel: state.presupuestos.isCancel,
  loading: state.presupuestos.loading,
  error: state.presupuestos.error,
  vaciarCarritoSuccess: state.presupuestos.vaciarCarritoSuccess,
});

export default connect(mapStateToProps)(Carrito);
